import { request, requestWithMeta } from './client.js'

export type BreakerState = 'TERTUTUP' | 'TERBUKA' | 'SETENGAH_TERBUKA'
export type JobStatus = 'BERJALAN' | 'SELESAI' | 'GAGAL' | 'DIBATALKAN'

export interface PullJob {
  readonly id: string
  readonly status: JobStatus
  readonly since: string | null
  readonly startedBy: string
  readonly startedAt: string
  readonly finishedAt: string | null
  readonly lastCompletedPage: number
  readonly reportedTotal: number | null
  readonly examined: number
  readonly created: number
  readonly unchanged: number
  readonly rejected: number
  readonly mediaMirrored: number
  /**
   * Dapat `undefined` bila endpoint tidak menyertakannya, dan `null` bila
   * penarikan belum menghasilkan temuan. Keduanya berarti "tidak ada yang
   * dapat ditampilkan"; membedakannya di tipe mencegah `.length` dipanggil
   * pada nilai yang tidak ada.
   */
  readonly findings: readonly Finding[] | null | undefined
  readonly lastError: string | null
  /** Sama seperti `findings`: dapat tidak ada sama sekali. */
  readonly categoryReport: CategoryReport | null | undefined
  readonly scheduled: boolean
}

export interface CategoryDiscrepancy {
  readonly category: string
  readonly reported: number
  readonly local: number
  readonly kind: 'KURANG' | 'LEBIH'
  readonly delta: number
  readonly reason: string
}

export interface CategoryReport {
  readonly balanced: boolean
  readonly categories: number
  readonly discrepancies: readonly CategoryDiscrepancy[]
}

export interface Finding {
  readonly caseCode: string
  readonly reason: string
  readonly detail?: readonly { field: string; message: string }[]
}

export interface ConnectorHealth {
  /** Hanya prefiks; token utuh tidak pernah keluar dari server. */
  readonly kredensial: string
  readonly breaker: { readonly state: BreakerState; readonly consecutiveFailures: number }
  readonly permintaan: number
  readonly pengulangan: number
  readonly kegagalanTerakhir: string | null
  readonly penarikanTerakhir: PullJob | null
  readonly jumlahStation: number
}

export interface StationSummary {
  readonly id: string
  readonly sourceCode: string
  readonly version: number
  readonly title: string
  readonly categoryName: string
  readonly skdiCode: string | null
  readonly skdiLevel: string | null
  readonly suggestedDurationMin: number | null
  readonly contentHash: string
  readonly pulledAt: string
}

export interface RubricItem {
  readonly competencyOrder: number
  readonly competencyName: string
  readonly score0Description: string | null
  readonly score1Description: string | null
  readonly score2Description: string | null
  readonly score3Description: string | null
  readonly weight: number
}

export interface StationMedia {
  readonly objectKey: string
  readonly mimeType: string
  readonly sizeBytes: number
  readonly contentHash: string
  readonly sourceUrl: string | null
}

export interface StationVersion extends StationSummary {
  readonly description: string | null
  readonly globalRatingCriteria: Record<string, string> | null
  readonly attribution: {
    readonly authorName?: string
    readonly authorInstitution?: string
    readonly references?: readonly string[]
    readonly skdiDescription?: string
  } | null
  readonly rubricItems: readonly RubricItem[]
  readonly vivaQuestions: readonly { readonly order: number; readonly questionText: string }[]
  readonly media: readonly StationMedia[]
  readonly requirement: {
    readonly layout: string | null
    readonly staff: string | null
    readonly manikin: string | null
    readonly tools: string | null
  } | null
}

export async function health(): Promise<ConnectorHealth> {
  return request<ConnectorHealth>('/api/v1/katalog/konektor')
}

export async function listStations(
  page: number,
): Promise<{ rows: readonly StationSummary[]; total: number }> {
  const { data, total } = await requestWithMeta<readonly StationSummary[]>(
    `/api/v1/katalog/station?page=${page}&limit=25`,
  )
  return { rows: data, total }
}

export async function stationVersions(sourceCode: string): Promise<readonly StationVersion[]> {
  return request<readonly StationVersion[]>(
    `/api/v1/katalog/station/${encodeURIComponent(sourceCode)}`,
  )
}

export async function listJobs(): Promise<readonly PullJob[]> {
  return request<readonly PullJob[]>('/api/v1/katalog/penarikan')
}

export async function getJob(id: string): Promise<PullJob> {
  return request<PullJob>(`/api/v1/katalog/penarikan/${id}`)
}

export async function startPull(input: { since?: string; resumeFrom?: string } = {}): Promise<{
  readonly id: string
  readonly lanjutDariHalaman: number
}> {
  return request('/api/v1/katalog/penarikan', { method: 'POST', body: input })
}
